import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Building2, MessageSquareText, History, Settings2, Users, 
  TrendingUp, CalendarDays, Trophy, LogOut, PieChart, Globe, Briefcase, 
  Sparkles, MoreHorizontal, X, Crown
} from "lucide-react";
import { useState, memo, useMemo, useCallback, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useLanguage } from "@/contexts/LanguageContext";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import NotificationBell from "@/components/NotificationBell";
import { useWebSocket } from "@/hooks/useWebSocket";

// Memoized navigation item to prevent re-renders
const NavItem = memo(({ item, isActive, isMobile = false }) => {
  const Icon = item.icon;
  
  if (isMobile) {
    return (
      <Link
        to={item.path}
        data-testid={`mobile-nav-${item.path.slice(1) || 'dashboard'}`}
        className="flex flex-col items-center justify-center flex-1 h-full relative"
      >
        {isActive && (
          <div className="absolute top-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-[#190019] rounded-full" />
        )}
        <div className={`p-3 rounded-xl ${
          isActive 
            ? 'bg-[#190019]' 
            : 'bg-transparent'
        }`}>
          <Icon className={`w-5 h-5 ${isActive ? 'text-[#fbe4d8]' : 'text-[#4a1a4a]'}`} strokeWidth={1.5} />
        </div>
        <span className={`text-[10px] mt-1 font-bold ${
          isActive ? 'text-[#190019]' : 'text-[#4a1a4a]'
        }`}>{item.shortLabel}</span>
      </Link>
    );
  }

  return (
    <Link
      to={item.path}
      data-testid={`nav-${item.path.slice(1) || 'dashboard'}`}
      className={`nav-item-luxury ${isActive ? 'active' : ''}`}
    >
      <Icon className={`nav-icon w-5 h-5 ${isActive ? 'text-[#fbe4d8]' : ''}`} strokeWidth={1.5} />
      <span className="text-sm font-semibold">{item.label}</span>
      {isActive && (
        <div className="mr-auto w-2 h-2 bg-[#fbe4d8] rounded-full" />
      )}
    </Link>
  );
});

NavItem.displayName = 'NavItem';

const Layout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { language, toggleLanguage } = useLanguage();
  const { t } = useTranslation();
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);

  const isAdmin = user?.role === "admin";
  
  // Get token from localStorage for WebSocket
  const token = localStorage.getItem('auth_token');
  
  // WebSocket for real-time notifications
  const { isConnected } = useWebSocket(token, (newMessage) => {
    // Handle new message - refresh conversations if on that page
    if (location.pathname === '/conversations' || location.pathname === '/messages') {
      // Trigger a refresh event
      window.dispatchEvent(new CustomEvent('newMessage', { detail: newMessage }));
    }
  });

  const handleLogout = useCallback(async () => {
    await logout();
    navigate("/login");
  }, [logout, navigate]);

  // التحقق من صلاحيات المستخدم
  // فقط مسؤول النظام الرئيسي (admin الأصلي) لديه صلاحية كاملة
  const isPrimaryAdmin = isAdmin && (user?.username === 'admin' || user?.email === 'admin@aqaarpro.com');
  
  // Check message permissions
  // - مسؤول النظام الرئيسي: يرى كل شيء تلقائياً
  // - المدراء والموظفين: يحتاجون صلاحية من مسؤول النظام
  const canViewLiveMessages = isPrimaryAdmin || user?.can_view_live_messages === true;
  const canViewConversationHistory = isPrimaryAdmin || user?.can_view_conversation_history === true;

  const menuItems = useMemo(() => [
    { path: "/", icon: LayoutDashboard, label: t('dashboard'), shortLabel: t('dashboard') },
    { path: "/leads", icon: Users, label: t('leads'), shortLabel: t('leads') },
    { path: "/pipeline", icon: TrendingUp, label: t('pipeline'), shortLabel: t('pipeline') },
    { path: "/products", icon: Building2, label: t('properties'), shortLabel: t('properties') },
    { path: "/appointments", icon: CalendarDays, label: t('appointments'), shortLabel: t('appointments') },
    // Only show messages if user has permission
    ...(canViewLiveMessages ? [
      { path: "/messages", icon: MessageSquareText, label: t('messages'), shortLabel: t('messages') }
    ] : []),
    // Only show conversation history if user has permission
    ...(canViewConversationHistory ? [
      { path: "/conversations", icon: History, label: t('conversations'), shortLabel: t('conversations') }
    ] : []),
    ...(isAdmin ? [
      { path: "/team", icon: Briefcase, label: t('team'), shortLabel: t('team') },
      { path: "/leaderboard", icon: Trophy, label: t('leaderboard'), shortLabel: t('leaderboard') },
      { path: "/reports", icon: PieChart, label: t('reports'), shortLabel: t('reports') },
    ] : []),
    { path: "/settings", icon: Settings2, label: t('settings'), shortLabel: t('settings') },
  ], [t, isAdmin, canViewLiveMessages, canViewConversationHistory]);

  return (
    <div className="flex min-h-screen flex-col md:flex-row" style={{ background: 'linear-gradient(135deg, #fbe4d8 0%, #fdf3ed 50%, #fff5ef 100%)' }} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Mobile Header - ثابت بدون حركة */}
      <header className="md:hidden glass-heavy text-white p-4 flex items-center justify-between sticky top-0 z-50" style={{ transform: 'none', animation: 'none' }}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-[#2d0a2d] to-[#4a1a4a] rounded-xl flex items-center justify-center purple-glow">
            <Sparkles className="w-5 h-5 text-[#fbe4d8]" />
          </div>
          <div>
            <h1 className="text-lg font-bold tracking-tight text-[#190019]">{t('appName')}</h1>
            {user && <p className="text-xs text-[#4a1a4a]">{user.name}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <NotificationBell />
          <Button
            onClick={toggleLanguage}
            variant="ghost"
            size="sm"
            className="text-[#4a1a4a] hover:text-[#190019] hover:bg-[#190019]/10 rounded-xl"
            data-testid="mobile-lang-btn"
          >
            <Globe className="w-4 h-4" />
          </Button>
          <Button
            onClick={handleLogout}
            variant="ghost"
            size="sm"
            className="text-red-600 hover:text-red-700 hover:bg-red-500/10 rounded-xl"
            data-testid="mobile-logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      {/* Desktop Sidebar - ثابت بدون حركة */}
      <aside className="hidden md:flex w-72 sidebar-luxury flex-col shrink-0" style={{ position: 'sticky', top: 0, height: '100vh' }}>
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-[#fbe4d8] to-[#d4a693] rounded-xl flex items-center justify-center peach-glow">
              <Sparkles className="w-6 h-6 text-[#190019]" />
            </div>
            <div>
              <h1 className="text-xl font-black text-white tracking-tight">{t('appName')}</h1>
              <p className="text-xs text-slate-500">{t('appDescription')}</p>
            </div>
          </div>
        </div>

        {/* User Info */}
        {user && (
          <div className="p-5 border-b border-white/5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-gradient-to-br from-[#fbe4d8]/30 to-[#d4a693]/20 rounded-xl flex items-center justify-center peach-border">
                <span className="text-xl font-black text-[#fbe4d8]">
                  {user.name?.charAt(0)}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-bold text-white truncate">{user.name}</p>
                <div className="flex items-center gap-1.5">
                  {user.role === "admin" && <Crown className="w-3 h-3 text-[#fbe4d8]" />}
                  <p className="text-xs text-[#fbe4d8]/80">
                    {user.role === "admin" ? t('admin') : t('salesAgent')}
                  </p>
                </div>
              </div>
            </div>
            <div className="flex gap-2 items-center">
              <NotificationBell variant="dark" />
              <Button
                onClick={toggleLanguage}
                variant="ghost"
                size="sm"
                className="flex-1 bg-[#fbe4d8]/20 hover:bg-[#fbe4d8]/30 text-[#fbe4d8] border border-[#fbe4d8]/30 text-sm font-bold rounded-xl"
                data-testid="lang-btn"
              >
                <Globe className="w-4 h-4 ml-1" />
                {language === 'ar' ? 'EN' : 'AR'}
              </Button>
              <Button
                onClick={handleLogout}
                variant="ghost"
                size="sm"
                className="flex-1 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 text-xs rounded-xl"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4" />
                <span className="mx-1.5">{t('logout')}</span>
              </Button>
            </div>
          </div>
        )}

        {/* Menu */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {menuItems.map((item) => (
            <NavItem 
              key={item.path} 
              item={item} 
              isActive={location.pathname === item.path} 
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="p-4 border-t border-white/5">
          <div className="glass-light rounded-xl p-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs text-slate-500">نظام CRM</p>
              <div className="flex items-center gap-1.5">
                <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse" />
                <span className="text-xs text-emerald-400 font-semibold">{t('active')}</span>
              </div>
            </div>
            <p className="text-[10px] text-[#6b2d6b]">
              Powered by <span className="purple-text font-bold">HELCOO AI</span>
            </p>
          </div>
        </div>
      </aside>

      {/* Main Content - المحتوى يتحرك داخل هذا الـ wrapper الثابت */}
      <main className="flex-1 overflow-auto pb-24 md:pb-0 relative">
        <div className="min-h-full">
          <Outlet />
        </div>
      </main>

      {/* Mobile Bottom Navigation - ثابتة بدون حركة */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-lg border-t border-[#190019]/10 z-50 safe-area-bottom" style={{ transform: 'none', animation: 'none' }}>
        <div className="flex justify-around items-center h-20 px-2">
          {menuItems.slice(0, 4).map((item) => (
            <NavItem 
              key={item.path} 
              item={item} 
              isActive={location.pathname === item.path}
              isMobile={true}
            />
          ))}
          
          <button
            onClick={() => setMoreMenuOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full"
            data-testid="mobile-nav-more"
          >
            <div className={`p-3 rounded-xl ${moreMenuOpen ? 'bg-[#190019]' : 'bg-transparent'}`}>
              <MoreHorizontal className={`w-5 h-5 ${moreMenuOpen ? 'text-[#fbe4d8]' : 'text-[#4a1a4a]'}`} strokeWidth={1.5} />
            </div>
            <span className={`text-[10px] mt-1 font-bold ${moreMenuOpen ? 'text-[#190019]' : 'text-[#4a1a4a]'}`}>
              {t('more')}
            </span>
          </button>
        </div>
      </nav>

      {/* More Menu */}
      {moreMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[60]" onClick={() => setMoreMenuOpen(false)}>
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
          
          <div 
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-3xl p-6 pb-10 animate-slide-up border-t border-[#190019]/10"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="w-12 h-1.5 bg-[#190019]/20 rounded-full mx-auto mb-6" />
            
            <button
              onClick={() => setMoreMenuOpen(false)}
              className="absolute top-5 left-5 p-2 rounded-xl hover:bg-[#190019]/10"
            >
              <X className="w-5 h-5 text-[#4a1a4a]" />
            </button>
            
            <h3 className="text-center text-lg font-black text-[#190019] mb-6">{t('more')}</h3>
            
            <div className="grid grid-cols-4 gap-3">
              {menuItems.slice(4).map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMoreMenuOpen(false)}
                    data-testid={`more-menu-${item.path.slice(1)}`}
                    className="flex flex-col items-center py-3 rounded-xl hover:bg-[#190019]/5"
                  >
                    <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-2 ${
                      isActive ? 'bg-[#190019]' : 'bg-[#fbe4d8]/50'
                    }`}>
                      <Icon className={`w-6 h-6 ${isActive ? 'text-[#fbe4d8]' : 'text-[#4a1a4a]'}`} strokeWidth={1.5} />
                    </div>
                    <span className={`text-xs font-bold ${isActive ? 'text-[#190019]' : 'text-[#4a1a4a]'}`}>
                      {item.shortLabel}
                    </span>
                  </Link>
                );
              })}
            </div>

            <div className="mt-6 pt-6 border-t border-[#190019]/10 flex gap-3">
              <Button
                onClick={() => { toggleLanguage(); setMoreMenuOpen(false); }}
                className="flex-1 h-12 bg-[#190019]/10 text-[#190019] hover:bg-[#190019]/20 rounded-xl border border-[#190019]/20"
              >
                <Globe className="w-5 h-5" />
                <span className="mx-2 font-bold">{language === 'ar' ? 'English' : 'العربية'}</span>
              </Button>
              <Button
                onClick={() => { handleLogout(); setMoreMenuOpen(false); }}
                className="flex-1 h-12 bg-red-500/10 text-red-600 hover:bg-red-500/20 rounded-xl border border-red-500/20"
              >
                <LogOut className="w-5 h-5" />
                <span className="mx-2 font-bold">{t('logout')}</span>
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default memo(Layout);
